'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  format,
  isValid,
  parse,
  isBefore,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  setHours,
  setMinutes,
  getHours,
  getMinutes,
} from 'date-fns';

interface DateTimePickerProps {
  value?: string;
  onChange: (value: string) => void;
  min?: string;
  placeholder?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  'data-field'?: string;
}

export function DateTimePicker({
  value,
  onChange,
  min,
  placeholder = 'Select date & time',
  className,
  disabled = false,
  error,
  'data-field': dataField,
}: DateTimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [popoverPos, setPopoverPos] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Helper to parse input datetime string (YYYY-MM-DDTHH:mm or ISO or YYYY-MM-DD HH:mm)
  const parseInputValue = (val?: string): Date | null => {
    if (!val || !val.trim()) return null;
    const clean = val.trim();
    // Try YYYY-MM-DDTHH:mm
    let d = parse(clean.slice(0, 16), "yyyy-MM-dd'T'HH:mm", new Date());
    if (isValid(d)) return d;
    // Try YYYY-MM-DD HH:mm
    d = parse(clean.slice(0, 16), 'yyyy-MM-dd HH:mm', new Date());
    if (isValid(d)) return d;
    // Try native Date constructor
    const native = new Date(clean);
    return isValid(native) ? native : null;
  };

  const selectedDate = parseInputValue(value);
  const minDate = parseInputValue(min);

  const [viewDate, setViewDate] = useState<Date>(() => selectedDate || new Date());

  const prevIsOpenRef = useRef(false);
  const prevValueRef = useRef(value);

  const updatePosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const popoverWidth = window.innerWidth < 480 ? Math.min(340, window.innerWidth - 24) : 470;
    const estimatedHeight = 440;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldOpenUp = spaceBelow < 380 && spaceAbove > spaceBelow;

    let top = shouldOpenUp ? rect.top - estimatedHeight - 8 : rect.bottom + 8;
    if (top < 8) top = 8;
    if (top + estimatedHeight > window.innerHeight - 8) {
      top = Math.max(8, window.innerHeight - estimatedHeight - 8);
    }

    let left = rect.left;
    if (left + popoverWidth > window.innerWidth - 16) {
      left = Math.max(16, window.innerWidth - popoverWidth - 16);
    }
    if (left < 16) left = 16;

    setPopoverPos({
      top,
      left,
      width: popoverWidth,
      maxHeight: Math.min(window.innerHeight - 24, Math.max(320, shouldOpenUp ? spaceAbove - 16 : spaceBelow - 16)),
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();

    const handleScrollOrResize = () => {
      updatePosition();
    };

    window.addEventListener('resize', handleScrollOrResize);
    window.addEventListener('scroll', handleScrollOrResize, true);

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        popoverRef.current &&
        !popoverRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    // Reset viewDate only on transition to open OR if external value changed
    if (!prevIsOpenRef.current || prevValueRef.current !== value) {
      if (selectedDate) {
        setViewDate(selectedDate);
      }
    }

    prevIsOpenRef.current = isOpen;
    prevValueRef.current = value;

    return () => {
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, value, selectedDate, updatePosition]);

  const formatOutput = (d: Date): string => {
    return format(d, "yyyy-MM-dd'T'HH:mm");
  };

  const handleDaySelect = (day: Date) => {
    const current = selectedDate || new Date();
    let updated = setHours(day, getHours(current));
    updated = setMinutes(updated, getMinutes(current));
    onChange(formatOutput(updated));
  };

  const handleTimeChange = (hours24: number, minutes: number) => {
    const current = selectedDate || new Date();
    let updated = setHours(current, hours24);
    updated = setMinutes(updated, minutes);
    onChange(formatOutput(updated));
  };

  const handlePrevMonth = () => setViewDate(subMonths(viewDate, 1));
  const handleNextMonth = () => setViewDate(addMonths(viewDate, 1));

  const start = startOfMonth(viewDate);
  const end = endOfMonth(viewDate);
  const startDay = start.getDay();
  const paddingDays = Array.from({ length: startDay }).map((_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() - (startDay - i));
    return d;
  });
  const daysInMonth = eachDayOfInterval({ start, end });
  const endDay = end.getDay();
  const trailingDays = Array.from({ length: 6 - endDay }).map((_, i) => {
    const d = new Date(end);
    d.setDate(d.getDate() + i + 1);
    return d;
  });

  const allDays = [...paddingDays, ...daysInMonth, ...trailingDays];

  // Time components (12-hour format)
  const currentHours24 = selectedDate ? getHours(selectedDate) : getHours(new Date());
  const currentMinutes = selectedDate ? getMinutes(selectedDate) : getMinutes(new Date());

  const currentPeriod = currentHours24 >= 12 ? 'PM' : 'AM';
  const currentHours12 = currentHours24 % 12 === 0 ? 12 : currentHours24 % 12;

  const hoursOptions = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  const periodOptions: Array<'AM' | 'PM'> = ['AM', 'PM'];

  const convertTo24h = (h12: number, period: 'AM' | 'PM'): number => {
    if (period === 'AM') return h12 === 12 ? 0 : h12;
    return h12 === 12 ? 12 : h12 + 12;
  };

  // Formatted button display text
  const displayLabel = selectedDate
    ? format(selectedDate, 'dd/MM/yyyy, hh:mm a')
    : placeholder;

  const toggleOpen = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;
    if (e.key === 'Escape' || e.key === 'Enter') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  return (
    <div className={cn('relative w-full', className)} ref={containerRef} onKeyDown={handleKeyDown}>
      <button
        type="button"
        disabled={disabled}
        data-field={dataField}
        onClick={toggleOpen}
        className={cn(
          'w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-xl border transition-all duration-200 outline-none text-left',
          'bg-[#FDFBF7] text-[#2D3536] border-[#E5E2D9] hover:border-[#5F6F52] hover:bg-white',
          isOpen && 'border-[#5F6F52] ring-2 ring-[#5F6F52]/20 bg-white',
          error && 'border-red-400 focus:ring-red-200',
          disabled && 'opacity-60 cursor-not-allowed bg-slate-100',
          !selectedDate && 'text-slate-400 font-normal'
        )}
      >
        <span className="truncate pr-2">{displayLabel}</span>
        <CalendarIcon className="w-4 h-4 text-[#5F6F52] shrink-0 opacity-80" />
      </button>

      {isOpen && mounted && popoverPos && typeof document !== 'undefined' && createPortal(
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            top: `${popoverPos.top}px`,
            left: `${popoverPos.left}px`,
            width: `${popoverPos.width}px`,
            maxHeight: `${popoverPos.maxHeight}px`,
          }}
          className="bg-white border border-[#E5E2D9] shadow-[0_12px_40px_rgba(0,0,0,0.18)] rounded-2xl p-4 sm:p-6 text-[#2D3536] flex flex-col overflow-visible z-50"
        >
          {/* Header */}
          <div className="flex items-center justify-between pb-3 border-b border-[#E5E2D9]/70">
            <span className="text-sm font-bold text-[#2D3536] tracking-tight">
              {format(viewDate, 'MMMM yyyy')}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handlePrevMonth}
                className="p-1.5 hover:bg-[#5F6F52]/10 text-[#2D3536] rounded-lg transition-colors"
                title="Previous Month"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleNextMonth}
                className="p-1.5 hover:bg-[#5F6F52]/10 text-[#2D3536] rounded-lg transition-colors"
                title="Next Month"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Main Grid: Left Calendar, Right Time Picker */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 sm:gap-6 pt-4 overflow-y-auto overflow-x-hidden pb-2 min-h-0">
            {/* Calendar */}
            <div>
              <div className="grid grid-cols-7 gap-1 text-center mb-2">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                  <div key={i} className="text-[11px] font-semibold text-[#737D7E] uppercase">
                    {d}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 text-xs">
                {allDays.map((day, i) => {
                  const isCurrentMonth = isSameMonth(day, viewDate);
                  const isSelected = selectedDate && isSameDay(day, selectedDate);
                  const isToday = isSameDay(day, new Date());
                  const isDisabledDay = minDate ? isBefore(day, minDate) && !isSameDay(day, minDate) : false;

                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={isDisabledDay}
                      onClick={() => handleDaySelect(day)}
                      className={cn(
                        'h-8 w-8 rounded-full flex items-center justify-center transition-all text-xs cursor-pointer',
                        isSelected
                          ? 'bg-[#5F6F52] text-white font-bold shadow-md'
                          : isToday
                          ? 'bg-[#F4F4F5] text-[#2D3536] font-semibold border border-[#E5E2D9]'
                          : isCurrentMonth
                          ? 'hover:bg-[#5F6F52]/10 text-[#2D3536] font-medium'
                          : 'text-slate-300 font-medium',
                        isDisabledDay && 'opacity-30 cursor-not-allowed hover:bg-transparent'
                      )}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Time Selector Columns */}
            <div className="bg-[#FDFBF7] rounded-xl border border-[#E5E2D9]/60 p-3 flex gap-2 items-start h-[260px]">
              {/* Hours Column */}
              <div className="flex flex-col gap-1 h-full overflow-y-auto w-12 sm:w-14 pr-2 scrollbar-thin">
                <div className="text-[10px] font-bold text-[#737D7E] text-center pb-2 mb-1 border-b border-[#E5E2D9]/60 sticky top-0 bg-[#FDFBF7] z-10">HH</div>
                {hoursOptions.map((h) => {
                  const isSelectedH = currentHours12 === h;
                  return (
                    <button
                      key={h}
                      type="button"
                      onClick={() => handleTimeChange(convertTo24h(h, currentPeriod), currentMinutes)}
                      className={cn(
                        'w-full px-2 py-1.5 text-xs rounded-full font-medium text-center transition-colors shrink-0',
                        isSelectedH
                          ? 'bg-[#5F6F52] text-white font-bold shadow-md'
                          : 'hover:bg-[#5F6F52]/10 text-[#2D3536]'
                      )}
                    >
                      {String(h).padStart(2, '0')}
                    </button>
                  );
                })}
              </div>

              {/* Minutes Column */}
              <div className="flex flex-col gap-1 h-full overflow-y-auto w-12 sm:w-14 pr-2 scrollbar-thin">
                <div className="text-[10px] font-bold text-[#737D7E] text-center pb-2 mb-1 border-b border-[#E5E2D9]/60 sticky top-0 bg-[#FDFBF7] z-10">MM</div>
                {Array.from({ length: 12 }).map((_, idx) => {
                  const m = idx * 5;
                  const isSelectedM = Math.abs(currentMinutes - m) < 3;
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => handleTimeChange(currentHours24, m)}
                      className={cn(
                        'w-full px-2 py-1.5 text-xs rounded-full font-medium text-center transition-colors shrink-0',
                        isSelectedM
                          ? 'bg-[#5F6F52] text-white font-bold shadow-md'
                          : 'hover:bg-[#5F6F52]/10 text-[#2D3536]'
                      )}
                    >
                      {String(m).padStart(2, '0')}
                    </button>
                  );
                })}
              </div>

              {/* AM/PM Column */}
              <div className="flex flex-col gap-1 h-full w-12 sm:w-14 pr-2">
                <div className="text-[10px] font-bold text-[#737D7E] text-center pb-2 mb-1 border-b border-[#E5E2D9]/60 sticky top-0 bg-[#FDFBF7] z-10">AM/PM</div>
                {periodOptions.map((p) => {
                  const isSelectedP = currentPeriod === p;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => handleTimeChange(convertTo24h(currentHours12, p), currentMinutes)}
                      className={cn(
                        'w-full px-2 py-1.5 text-xs rounded-full font-medium text-center transition-colors shrink-0',
                        isSelectedP
                          ? 'bg-[#5F6F52] text-white font-bold shadow-md'
                          : 'hover:bg-[#5F6F52]/10 text-[#2D3536]'
                      )}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Action Footer */}
          <div className="flex items-center justify-between pt-4 mt-2 border-t border-[#E5E2D9] text-xs shrink-0 bg-white z-20">
            <button
              type="button"
              onClick={() => {
                onChange('');
                setIsOpen(false);
              }}
              className="text-[#737D7E] hover:text-[#2D3536] font-medium transition-colors"
            >
              Clear
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  const now = new Date();
                  onChange(formatOutput(now));
                  setViewDate(now);
                }}
                className="text-[#5F6F52] font-semibold hover:underline"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="px-3 py-1 bg-[#5F6F52] text-white font-medium rounded-lg hover:bg-[#4E5C43] transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
